import type React from "react"
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { Button } from "../ui/button"
import { PageLoader } from "../ui/page-loader"

interface AdminEdgeGateProps {
  children: React.ReactNode
}

type GateStatus = "pending" | "allowed" | "error"

/**
 * Soft React Router entry into `/admin` never hits CloudFront, so the WAF VPN
 * gate would not run. Probe the current admin URL and fail closed:
 * 200 allows the admin tree, 401 hard-replaces so the browser shows the
 * branded WAF page, and any other status or network error stays blocked
 * with a retry. Local vite and production-without-WAF both serve 200.
 */
export function AdminEdgeGate({ children }: AdminEdgeGateProps) {
  const { pathname, search } = useLocation()
  const [status, setStatus] = useState<GateStatus>("pending")
  const [retry, setRetry] = useState(0)
  // Capture the URL at mount so in-admin soft navigations do not re-probe.
  const probePath = useRef(`${pathname}${search}`).current

  useEffect(() => {
    let cancelled = false
    setStatus("pending")
    void retry

    fetch(probePath, { cache: "no-store", credentials: "same-origin", redirect: "manual" })
      .then((res) => {
        if (cancelled) return
        if (res.status === 401) {
          window.location.replace(probePath)
          return
        }
        if (res.status === 200) {
          setStatus("allowed")
          return
        }
        setStatus("error")
      })
      .catch(() => {
        if (!cancelled) setStatus("error")
      })

    return () => {
      cancelled = true
    }
  }, [probePath, retry])

  if (status === "allowed") {
    return <>{children}</>
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg">
        <p className="text-content-2">Unable to verify network access.</p>
        <Button type="button" onClick={() => setRetry((n) => n + 1)}>
          Retry
        </Button>
      </div>
    )
  }

  return <PageLoader className="min-h-screen" />
}
